import { useEffect, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import InputField from "../../../../components/input";
import TextareaField from "../../../../components/textarea";
import DatePickerField from "../../../../components/datePicker";
import Loader from "../../../../components/loaders/loader";
import HomeEnquiry from "../../../../components/confirmationPopup/hEnquiry";
import { useAppSelector } from "../../../../store";
import { POST } from "../../../../config/httpMain";
import EnquiryApiUrl from "../../../../config/apiURL/enquiryApiUrl";
import { APPNAME, COMPANYCODE, SOURCE } from "../../../../constants/constant";
import {
  convertLocalToUTC,
  formatDateyyyyMMdd,
  getDateMonthRange,
  getUserTimeZone,
  unlockBodyScroll,
} from "../../../../utility/utils";
import { errorHandler } from "../../../../utility/errorHandler";
import "./planYourTrip.scss";

type EnquiryTab = "flight" | "hotel" | "insurance";

const WHATSAPP_URL = "https://wa.me/919739737528";

const TAB_CONFIG = [
  {
    id: "flight" as EnquiryTab,
    title: "Flight Enquiry",
    icon: "bx bxs-plane-alt",
    bullets: ["Best Airfares", "Multiple Airlines", "Hassle-free Booking"],
  },
  {
    id: "hotel" as EnquiryTab,
    title: "Hotel Enquiry",
    icon: "bx bxs-buildings",
    bullets: ["Best Hotel Deals", "Wide Range of Options", "Secure & Easy Booking"],
  },
  {
    id: "insurance" as EnquiryTab,
    title: "Travel Insurance Enquiry",
    icon: "bx bxs-shield-plus",
    bullets: ["Comprehensive Coverage", "Quick Claim Support", "Travel with Peace of Mind"],
  },
];

const ADULT_OPTIONS = Array.from({ length: 9 }, (_, i) => i + 1);
const CHILD_OPTIONS = Array.from({ length: 7 }, (_, i) => i);
const INFANT_OPTIONS = Array.from({ length: 5 }, (_, i) => i);
const ROOM_OPTIONS = Array.from({ length: 5 }, (_, i) => i + 1);

const HOTEL_PREFERENCES = [
  { value: "", label: "Select hotel category" },
  { value: "3 Star", label: "3 Star" },
  { value: "4 Star", label: "4 Star" },
  { value: "5 Star", label: "5 Star" },
  { value: "Boutique", label: "Boutique" },
  { value: "Resort", label: "Resort" },
];

const INSURANCE_TYPES = [
  { value: "", label: "Select type" },
  { value: "Single Trip", label: "Single Trip" },
  { value: "Annual Multi-Trip", label: "Annual Multi-Trip" },
  { value: "Student", label: "Student" },
  { value: "Senior Citizen", label: "Senior Citizen" },
];

const SUM_INSURED_OPTIONS = [
  { value: "", label: "Select amount" },
  { value: "50000", label: "₹50,000" },
  { value: "100000", label: "₹1,00,000" },
  { value: "200000", label: "₹2,00,000" },
  { value: "500000", label: "₹5,00,000" },
  { value: "1000000", label: "₹10,00,000" },
];

const enquiryTypeMap: Record<EnquiryTab, string> = {
  flight: "FlightEnquiry",
  hotel: "HotelEnquiry",
  insurance: "TravelInsuranceEnquiry",
};

const enquiryTypeRemarkMap: Record<EnquiryTab, string> = {
  flight: "Flight Enquiry",
  hotel: "Hotel Enquiry",
  insurance: "Travel Insurance Enquiry",
};

const PlanYourTripModal = ({
  showModal,
  setShowModal,
  initialTab = "flight",
}: {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  initialTab?: EnquiryTab;
}) => {
  const { user } = useAppSelector((state) => state.userSlice);
  const [activeTab, setActiveTab] = useState<EnquiryTab>(initialTab);

  useEffect(() => {
    if (showModal) {
      setActiveTab(initialTab);
    }
  }, [showModal, initialTab]);
  const [loader, setLoader] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const { minDate, maxDate } = getDateMonthRange();

  const initialValues = {
    fullName: "",
    emailId: "",
    mobileNo: "",
    mobileCountryCode: "+91",
    destination: "",
    from: "",
    to: "",
    startDate: null as Date | null,
    endDate: null as Date | null,
    adults: 1,
    children: 0,
    infants: 0,
    rooms: 1,
    hotelPreference: "",
    insuranceType: "",
    sumInsured: "",
    queries: "",
    disclaimer: false,
    timeZone: getUserTimeZone(),
  };

  const getValidationSchema = () => {
    const base = {
      fullName: Yup.string().required("Full Name is required"),
      mobileNo: Yup.string().required("Mobile Number is required"),
      mobileCountryCode: Yup.string().required("Country Code is required"),
      startDate: Yup.date().nullable().required("Start date is required"),
      endDate: Yup.date().nullable().required("End date is required"),
      disclaimer: Yup.boolean()
        .oneOf([true], "You must accept this")
        .required("You must accept this"),
    };

    if (activeTab === "flight") {
      return Yup.object().shape({
        ...base,
        from: Yup.string().required("From is required"),
        to: Yup.string().required("To is required"),
      });
    }

    if (activeTab === "insurance") {
      return Yup.object().shape({
        ...base,
        destination: Yup.string().required("This field is required"),
        insuranceType: Yup.string().required("Type of Insurance is required"),
        sumInsured: Yup.string().required("Sum Insured is required"),
      });
    }

    return Yup.object().shape({
      ...base,
      destination: Yup.string().required("This field is required"),
    });
  };

  const formatDisplayDate = (date: Date | null) =>
    date ? formatDateyyyyMMdd(date, "-") : "-";

  const buildQueries = (values: typeof initialValues) => {
    const lines: string[] = [
      `Enquiry Type: ${enquiryTypeRemarkMap[activeTab]}`,
      "Source: Home Page - Explore Deals",
    ];

    if (activeTab === "flight") {
      lines.push(`From: ${values.from}`);
      lines.push(`To: ${values.to}`);
      lines.push(`Departure: ${formatDisplayDate(values.startDate)}`);
      lines.push(`Return: ${formatDisplayDate(values.endDate)}`);
      lines.push(
        `Travellers: Adults ${values.adults}, Children ${values.children}, Infants ${values.infants}`
      );
    } else if (activeTab === "hotel") {
      lines.push(`Destination/City: ${values.destination}`);
      lines.push(`Check-in: ${formatDisplayDate(values.startDate)}`);
      lines.push(`Check-out: ${formatDisplayDate(values.endDate)}`);
      lines.push(
        `Guests/Rooms: Rooms ${values.rooms}, Adults ${values.adults}, Children ${values.children}`
      );
      if (values.hotelPreference) {
        lines.push(`Hotel Preference: ${values.hotelPreference}`);
      }
    } else {
      lines.push(`Destination/Travelling From: ${values.destination}`);
      lines.push(`Travel Start: ${formatDisplayDate(values.startDate)}`);
      lines.push(`Travel End: ${formatDisplayDate(values.endDate)}`);
      lines.push(
        `Travellers: Adults ${values.adults}, Children ${values.children}, Infants ${values.infants}`
      );
      lines.push(`Insurance Type: ${values.insuranceType}`);
      lines.push(`Sum Insured: ${values.sumInsured}`);
    }

    if (values.queries?.trim()) {
      lines.push(`Additional Requirements: ${values.queries.trim()}`);
    }

    return lines.join(" | ");
  };

  const restoreBodyScroll = () => {
    unlockBodyScroll();
    // Bootstrap can re-apply lock during fade-out; clear again after it finishes.
    window.setTimeout(unlockBodyScroll, 0);
    window.setTimeout(unlockBodyScroll, 350);
  };

  const handleClose = () => {
    setShowModal(false);
    setActiveTab(initialTab);
    restoreBodyScroll();
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setLoader(true);

    const payload = {
      packageId: 0,
      packageCode: "",
      packageName: "",
      fullName: values.fullName,
      emailId: values.emailId,
      mobileNo: values.mobileNo,
      mobileCountryCode: values.mobileCountryCode,
      departureCity:
        activeTab === "flight"
          ? `${values.from} - ${values.to}`
          : values.destination,
      timeZone: values.timeZone,
      departureUtcDate: convertLocalToUTC(
        values.startDate ? formatDateyyyyMMdd(values.startDate, "-") : null
      ),
      pax: Number(values.adults) + Number(values.children) + Number(values.infants),
      queries: buildQueries(values),
      tag: `FlightHotelDeals-${enquiryTypeRemarkMap[activeTab]}`,
      disclaimer: values.disclaimer,
      enquiryType: enquiryTypeMap[activeTab],
      source: SOURCE,
      companyCode: COMPANYCODE,
      appName: APPNAME,
      createdById: user?.id,
    };

    await addUpdateData(payload);
    handleClose();
    setShowConfirmation(true);

    setTimeout(() => {
      setLoader(false);
    }, 2000);
  };

  const addUpdateData = async (payload: any) => {
    await POST(EnquiryApiUrl.AddEnquiryNow, payload)
      .then((response: any) => {
        if (!response?.data?.isSuccess) {
          errorHandler("response", response);
        }
      })
      .catch((e: any) => {
        errorHandler("error", e);
      });
  };

  const destinationLabel =
    activeTab === "hotel"
      ? "Destination / City *"
      : "Country Visiting *";

  const startDateLabel =
    activeTab === "flight"
      ? "Departure Date *"
      : activeTab === "hotel"
        ? "Check-in Date *"
        : "Travel Start Date *";

  const endDateLabel =
    activeTab === "flight"
      ? "Return Date *"
      : activeTab === "hotel"
        ? "Check-out Date *"
        : "Travel End Date *";

  return (
    <>
      {loader && <Loader />}
      <Modal
        isOpen={showModal}
        toggle={handleClose}
        centered
        size="xl"
        className="plan-your-trip-modal"
        onClosed={restoreBodyScroll}
      >
        <ModalBody className="p-0">
          <div className="pyt-modal">
            <button
              type="button"
              className="pyt-close"
              aria-label="Close"
              onClick={handleClose}
            >
              <i className="bx bx-x"></i>
            </button>

            <div className="pyt-tabs">
              {TAB_CONFIG.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`pyt-tab pyt-tab--${tab.id} ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="pyt-tab-radio" aria-hidden="true" />
                  <span className={`pyt-tab-icon pyt-tab-icon--${tab.id}`}>
                    <i className={tab.icon}></i>
                  </span>
                  <div className="pyt-tab-content">
                    <h4>{tab.title}</h4>
                    <ul>
                      {tab.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </div>
                </button>
              ))}
            </div>

            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={getValidationSchema()}
              onSubmit={(values, actions) => {
                handleSubmit(values);
                actions.setSubmitting(false);
                actions.resetForm({ values: initialValues });
              }}
            >
              {({
                values,
                errors,
                touched,
                isSubmitting,
                handleChange,
                handleSubmit: formikSubmit,
                setFieldValue,
              }) => (
                <form
                  className={`pyt-form pyt-form--${activeTab}`}
                  onSubmit={(e) => {
                    e.preventDefault();
                    formikSubmit(e);
                  }}
                >
                  <div className="pyt-form-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="pyt-label">Full Name *</label>
                        <div className="input-icon-wrapper">
                          <i className="bx bx-user left-icon"></i>
                          <InputField
                            className={`form-control ps-5 ${errors.fullName && touched.fullName ? "is-invalid" : ""}`}
                            name="fullName"
                            placeholder="Enter your full name"
                            selectedValue={values.fullName}
                            onChange={handleChange}
                            maxLength={250}
                          />
                        </div>
                        {errors.fullName && touched.fullName && (
                          <small className="text-danger">{errors.fullName}</small>
                        )}
                      </div>

                      <div className="col-md-4">
                        <label className="pyt-label">Mobile Number *</label>
                        <div className="input-icon-wrapper">
                          <PhoneInput
                            country={"in"}
                            inputClass="phone-input"
                            buttonClass="phone-dropdown"
                            containerClass="phone-container"
                            placeholder="Enter mobile number"
                            enableSearch
                            countryCodeEditable={false}
                            value={values.mobileCountryCode + values.mobileNo}
                            onChange={(value, country: any) => {
                              const dialCode = country?.dialCode;
                              const numberOnly = value?.slice(dialCode?.length);
                              setFieldValue("mobileCountryCode", `+${dialCode}`);
                              setFieldValue("mobileNo", numberOnly);
                            }}
                          />
                        </div>
                        {errors.mobileNo && touched.mobileNo && (
                          <small className="text-danger">{errors.mobileNo}</small>
                        )}
                      </div>

                      <div className="col-md-4">
                        <label className="pyt-label">Email Address</label>
                        <div className="input-icon-wrapper">
                          <i className="bx bx-envelope left-icon"></i>
                          <InputField
                            type="email"
                            className="form-control ps-5"
                            name="emailId"
                            placeholder="Enter your email address"
                            selectedValue={values.emailId}
                            onChange={handleChange}
                            maxLength={500}
                          />
                        </div>
                      </div>

                      {activeTab === "flight" ? (
                        <div className="col-12">
                          <div className="row g-3">
                            <div className="col-md-3">
                              <label className="pyt-label">From *</label>
                              <div className="input-icon-wrapper">
                                <i className="bx bx-map-pin left-icon"></i>
                                <InputField
                                  className={`form-control ps-5 ${errors.from && touched.from ? "is-invalid" : ""}`}
                                  name="from"
                                  placeholder="Departure city"
                                  selectedValue={values.from}
                                  onChange={handleChange}
                                  maxLength={500}
                                />
                              </div>
                              {errors.from && touched.from && (
                                <small className="text-danger">{errors.from}</small>
                              )}
                            </div>
                            <div className="col-md-3">
                              <label className="pyt-label">To *</label>
                              <div className="input-icon-wrapper">
                                <i className="bx bxs-map left-icon"></i>
                                <InputField
                                  className={`form-control ps-5 ${errors.to && touched.to ? "is-invalid" : ""}`}
                                  name="to"
                                  placeholder="Arrival city"
                                  selectedValue={values.to}
                                  onChange={handleChange}
                                  maxLength={500}
                                />
                              </div>
                              {errors.to && touched.to && (
                                <small className="text-danger">{errors.to}</small>
                              )}
                            </div>
                            <div className="col-md-3">
                              <label className="pyt-label">{startDateLabel}</label>
                              <div className="input-icon-wrapper">
                                <i
                                  className="bx bx-calendar left-icon"
                                  onClick={() => startDateRef.current?.showPicker?.()}
                                ></i>
                                <DatePickerField
                                  name="startDate"
                                  className={`form-control ps-5 ${errors.startDate && touched.startDate ? "is-invalid" : ""}`}
                                  value={values.startDate}
                                  onChange={(event: any) => {
                                    setFieldValue("startDate", event || null);
                                  }}
                                  mode={"full"}
                                  placeholder={"Select date"}
                                  dateFormat={"dd/MM/yyyy"}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                />
                              </div>
                              {errors.startDate && touched.startDate && (
                                <small className="text-danger">{errors.startDate}</small>
                              )}
                            </div>
                            <div className="col-md-3">
                              <label className="pyt-label">{endDateLabel}</label>
                              <div className="input-icon-wrapper">
                                <i
                                  className="bx bx-calendar left-icon"
                                  onClick={() => endDateRef.current?.showPicker?.()}
                                ></i>
                                <DatePickerField
                                  name="endDate"
                                  className={`form-control ps-5 ${errors.endDate && touched.endDate ? "is-invalid" : ""}`}
                                  value={values.endDate}
                                  onChange={(event: any) => {
                                    setFieldValue("endDate", event || null);
                                  }}
                                  mode={"full"}
                                  placeholder={"Select date"}
                                  dateFormat={"dd/MM/yyyy"}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                />
                              </div>
                              {errors.endDate && touched.endDate && (
                                <small className="text-danger">{errors.endDate}</small>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="col-12">
                          <div className="row g-3">
                            <div className="col-md-4">
                              <label className="pyt-label">{destinationLabel}</label>
                              <div className="input-icon-wrapper">
                                <i className="bx bxs-map left-icon"></i>
                                <InputField
                                  className={`form-control ps-5 ${errors.destination && touched.destination ? "is-invalid" : ""}`}
                                  name="destination"
                                  placeholder="Enter destination or city"
                                  selectedValue={values.destination}
                                  onChange={handleChange}
                                  maxLength={500}
                                />
                              </div>
                              {errors.destination && touched.destination && (
                                <small className="text-danger">{errors.destination}</small>
                              )}
                            </div>

                            <div className="col-md-4">
                              <label className="pyt-label">{startDateLabel}</label>
                              <div className="input-icon-wrapper">
                                <i
                                  className="bx bx-calendar left-icon"
                                  onClick={() => startDateRef.current?.showPicker?.()}
                                ></i>
                                <DatePickerField
                                  name="startDate"
                                  className={`form-control ps-5 ${errors.startDate && touched.startDate ? "is-invalid" : ""}`}
                                  value={values.startDate}
                                  onChange={(event: any) => {
                                    setFieldValue("startDate", event || null);
                                  }}
                                  mode={"full"}
                                  placeholder={"Select date"}
                                  dateFormat={"dd/MM/yyyy"}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                />
                              </div>
                              {errors.startDate && touched.startDate && (
                                <small className="text-danger">{errors.startDate}</small>
                              )}
                            </div>

                            <div className="col-md-4">
                              <label className="pyt-label">{endDateLabel}</label>
                              <div className="input-icon-wrapper">
                                <i
                                  className="bx bx-calendar left-icon"
                                  onClick={() => endDateRef.current?.showPicker?.()}
                                ></i>
                                <DatePickerField
                                  name="endDate"
                                  className={`form-control ps-5 ${errors.endDate && touched.endDate ? "is-invalid" : ""}`}
                                  value={values.endDate}
                                  onChange={(event: any) => {
                                    setFieldValue("endDate", event || null);
                                  }}
                                  mode={"full"}
                                  placeholder={"Select date"}
                                  dateFormat={"dd/MM/yyyy"}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                />
                              </div>
                              {errors.endDate && touched.endDate && (
                                <small className="text-danger">{errors.endDate}</small>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === "hotel" ? (
                        <>
                          <div className="col-12">
                            <div className="pyt-section-title">Guests / Rooms</div>
                          </div>
                          <div className="col-md-4">
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-bed left-icon"></i>
                              <select
                                name="rooms"
                                className="form-control ps-5"
                                value={values.rooms}
                                onChange={handleChange}
                              >
                                {ROOM_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} Room{n > 1 ? "s" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <small className="pyt-hint">Rooms</small>
                          </div>
                          <div className="col-md-4">
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-user left-icon"></i>
                              <select
                                name="adults"
                                className="form-control ps-5"
                                value={values.adults}
                                onChange={handleChange}
                              >
                                {ADULT_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} Adult{n > 1 ? "s" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <small className="pyt-hint">Adults</small>
                          </div>
                          <div className="col-md-4">
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-child left-icon"></i>
                              <select
                                name="children"
                                className="form-control ps-5"
                                value={values.children}
                                onChange={handleChange}
                              >
                                {CHILD_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} Child{n !== 1 ? "ren" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <small className="pyt-hint">Children (2-11 yrs)</small>
                          </div>

                          <div className="col-12">
                            <label className="pyt-label">Hotel Preference (Optional)</label>
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bxs-star left-icon"></i>
                              <select
                                name="hotelPreference"
                                className="form-control ps-5"
                                value={values.hotelPreference}
                                onChange={handleChange}
                              >
                                {HOTEL_PREFERENCES.map((opt) => (
                                  <option key={opt.label} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="col-12">
                            <div className="pyt-section-title">Travellers</div>
                          </div>
                          <div className="col-md-4">
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-user left-icon"></i>
                              <select
                                name="adults"
                                className="form-control ps-5"
                                value={values.adults}
                                onChange={handleChange}
                              >
                                {ADULT_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} Adult{n > 1 ? "s" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <small className="pyt-hint">Adults (12+ yrs)</small>
                          </div>
                          <div className="col-md-4">
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-child left-icon"></i>
                              <select
                                name="children"
                                className="form-control ps-5"
                                value={values.children}
                                onChange={handleChange}
                              >
                                {CHILD_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} Child{n !== 1 ? "ren" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <small className="pyt-hint">Children (2-11 yrs)</small>
                          </div>
                          <div className="col-md-4">
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-baby-carriage left-icon"></i>
                              <select
                                name="infants"
                                className="form-control ps-5"
                                value={values.infants}
                                onChange={handleChange}
                              >
                                {INFANT_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} Infant{n !== 1 ? "s" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <small className="pyt-hint">Infants (Below 2 yrs)</small>
                          </div>
                        </>
                      )}

                      {activeTab === "insurance" && (
                        <>
                          <div className="col-12">
                            <div className="pyt-section-title">Insurance Details</div>
                          </div>
                          <div className="col-md-6">
                            <label className="pyt-label">Type of Insurance *</label>
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-shield left-icon"></i>
                              <select
                                name="insuranceType"
                                className={`form-control ps-5 ${errors.insuranceType && touched.insuranceType ? "is-invalid" : ""}`}
                                value={values.insuranceType}
                                onChange={handleChange}
                              >
                                {INSURANCE_TYPES.map((opt) => (
                                  <option key={opt.label} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {errors.insuranceType && touched.insuranceType && (
                              <small className="text-danger">{errors.insuranceType}</small>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="pyt-label">Sum Insured *</label>
                            <div className="input-icon-wrapper custom-select">
                              <i className="bx bx-check-shield left-icon"></i>
                              <select
                                name="sumInsured"
                                className={`form-control ps-5 ${errors.sumInsured && touched.sumInsured ? "is-invalid" : ""}`}
                                value={values.sumInsured}
                                onChange={handleChange}
                              >
                                {SUM_INSURED_OPTIONS.map((opt) => (
                                  <option key={opt.label} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {errors.sumInsured && touched.sumInsured && (
                              <small className="text-danger">{errors.sumInsured}</small>
                            )}
                          </div>
                        </>
                      )}

                      <div className="col-12">
                        <label className="pyt-label">Additional Requirements (Optional)</label>
                        <div className="input-icon-wrapper textarea-wrapper">
                          <i className="bx bx-pencil left-icon"></i>
                          <TextareaField
                            rows={3}
                            className="form-control ps-5"
                            name="queries"
                            placeholder="Tell us more about your requirements"
                            selectedValue={values.queries}
                            onChange={handleChange}
                            maxLength={4000}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pyt-footer">
                    <div className="form-check">
                      <InputField
                        type="checkbox"
                        id="pyt-disclaimer"
                        name="disclaimer"
                        checked={values.disclaimer}
                        onChange={handleChange}
                        className={`form-check-input ${errors.disclaimer && touched.disclaimer ? "is-invalid" : ""}`}
                      />
                      <label className="form-check-label" htmlFor="pyt-disclaimer">
                        I agree to the{" "}
                        <Link to="/privacy-policy" target="_blank">
                          Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link to="/terms-condition" target="_blank">
                          Terms & Conditions
                        </Link>
                        .
                      </label>
                      {errors.disclaimer && touched.disclaimer && (
                        <small className="text-danger d-block">{errors.disclaimer}</small>
                      )}
                    </div>

                    <div className="pyt-actions">
                      <button
                        type="submit"
                        className="pyt-btn pyt-btn-primary"
                        disabled={isSubmitting}
                      >
                        <i className="bx bxs-paper-plane"></i>
                        SUBMIT ENQUIRY
                      </button>
                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pyt-btn pyt-btn-outline"
                      >
                        <i className="bx bxl-whatsapp"></i>
                        CHAT ON WHATSAPP
                      </a>
                    </div>

                    <p className="pyt-secure">
                      <i className="bx bx-lock-alt"></i>
                      Your data is secure with us. We respect your privacy.
                    </p>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </ModalBody>
      </Modal>

      {showConfirmation && (
        <HomeEnquiry
          showConfirmation={showConfirmation}
          setShowConfirmation={setShowConfirmation}
        />
      )}
    </>
  );
};

export default PlanYourTripModal;
